import "./ProductDetailsEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import Grid from "@mui/material/Grid";
import FormikAutoCompleteDropdown
    from "../../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormSubTitleWithTooltipComponent
    from "../../../../../shared/components/form-sub-title-with-tooltip/FormSubTitleWithTooltipComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ChipComponent from "../../../../../shared/components/chip/ChipComponent";
import {AttachFile} from "@mui/icons-material";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../../store/reducers";
import CommonService from "../../../../../shared/services/common.service";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddIcon from "@mui/icons-material/Add";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import commonService from "../../../../../shared/services/common.service";

interface ProductDetailsEditComponentProps {
    productDetails: any;
    facilityId: string;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const productInformationValidationSchema = Yup.object({
        name: Yup.string()
            .required("Product Name is required")
            .nullable(),
        panelId: Yup.mixed().required("Panel Name is required").nullable(),
        productCode: Yup.string().required("Product Code is required").nullable(),
        quantity: Yup.string()
            .required("Available Quantity is required")
            .matches(/^[1-9][0-9]*$/, 'Please enter a valid number').test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false).nullable(),
        alertLevelStock: Yup.string().required("Alert Level Quantity is required")
            .matches(/^[1-9][0-9]*$/, 'Please enter a valid number').test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false).nullable(),
        price: Yup.string().required("Retail Price is required")
            .matches(/^[1-9][0-9]*$/, 'Please enter a valid number').test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false).nullable(),
        description: Yup.string().required("Description is required").nullable(),
        productCatalogLink: Yup.string().required("Catalog Link is required").nullable(),
        tagLine: Yup.string().required('Required').nullable(),
        methodOfCollection: Yup.mixed().required('Required').nullable(),
        resultingDays: Yup.string()
            .matches(/^\d+(\.\d+)?(-\d+(\.\d+)?)?$/, 'Please enter a valid number or range').required('Required')
            .test('is-positive', 'Please enter a positive number', (value) => value ? parseFloat(value) > 0 : false).nullable(),
        analytes: Yup.array().of(Yup.object({
            analyte: Yup.string().required('Required'),
        })).required('Required'),
    }
);

interface productInformationFormSchema {
    name: string;
    panelId: string;
    productCode: string;
    quantity: string;
    alertLevelStock: string;
    price: string;
    description: string;
    productCatalogLink: string;
    tagLine: string;
    methodOfCollection: string;
    resultingDays: string;
    analytes: Array<{
        analyte: string;
    }>;

}

const productInformationInitialValues: productInformationFormSchema = {
    name: "",
    panelId: "",
    productCode: "",
    quantity: "",
    alertLevelStock: "",
    price: "",
    description: "",
    productCatalogLink: "",
    tagLine: '',
    methodOfCollection: '',
    resultingDays: '',
    analytes: [{
        analyte: '',
    }]
};
const ProductDetailsEditComponent = (props: ProductDetailsEditComponentProps) => {

    const {onUpdateComplete, onEditCancel, productDetails} = props;

    const [productDetailsInitialValues, setProductDetailsInitialValues] = useState<any>(productInformationInitialValues);
    const productImageFileRef = useRef<HTMLInputElement | null>(null);
    const [productImages, setProductImages] = useState<any[]>([]);
    const [productImageAddingInProgress, setProductImageAddingInProgress] = useState<boolean>(false);
    const [productImageRemovingInProgress, setProductImageRemovingInProgress] = useState<boolean>(false);
    const [isProductImagesChanged, setIsProductImagesChanged] = useState<boolean>(false);

    const {
        testPanelList
    } = useSelector((state: IRootReducerState) => state.staticData);

    const handleTemplateAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = null;
            event.target.files = null;
            return;
        }
        const formData = new FormData();
        formData.append('image', file);
        setProductImageAddingInProgress(true);
        commonService._inventory.addProductImage(productDetails.id, formData)
            .then((response: IAPIResponseType<any>) => {
                setProductImageAddingInProgress(false);
                setProductImages((prevState: any[]) => {
                    return [...prevState, response.data];
                })
                setIsProductImagesChanged(true);
            })
            .catch((error: any) => {
                setProductImageAddingInProgress(false);
                commonService._alert.showToast(error.message, "error");
            })
        event.target.value = null;
        event.target.files = null;
    }, [productDetails]);

    const handleProductImageRemove = useCallback((image: any) => {
        const payload = {
            imageId: image.id
        }
        setProductImageRemovingInProgress(true);
        commonService._inventory.deleteProductImage(productDetails.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setProductImageRemovingInProgress(false);
                setProductImages((prevState: any[]) => {
                        return prevState.filter((item: any) => item.id !== image.id);
                    }
                )
                setIsProductImagesChanged(true);
            })
            .catch((error: any) => {
                setProductImageRemovingInProgress(false);
                commonService._alert.showToast(error.message, "error");
            })

    }, [productDetails]);

    useEffect(() => {
        if (productDetails) {
            setProductDetailsInitialValues(productDetails);
            setProductImages(productDetails?.images || [])
            setProductDetailsInitialValues((prevState: any) => {
                return {
                    ...prevState,
                    panelId: prevState.panelDetails,
                    analytes: !prevState?.analytes ? [{
                        analyte: '',
                    }] : prevState?.analytes.map((analyte: any) => {
                            return {
                                analyte: analyte
                            }
                        }
                    )
                }
            })
        }
    }, [productDetails]);

    const onSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors
    }: FormikHelpers<any>) => {
        const payload = {...values};
        payload.panelId = payload.panelId?.id;
        payload.analytes = payload.analytes?.map((analyte: any) => {
            return analyte.analyte;
        });
        setSubmitting(true);
        CommonService._inventory.editProduct(productDetails?.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                onUpdateComplete({...response?.data, images: productImages});
                CommonService._alert.showToast(response?.message || 'primary Contact details has been added', "success");
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [productDetails, onUpdateComplete, productImages]);


    return (
        <div className={'product-details-edit-component details-tab-data-content'}>
            <Formik
                validationSchema={productInformationValidationSchema}
                initialValues={productDetailsInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onSubmit}
            >
                {({isSubmitting, values, dirty, isValid, setFieldValue, validateForm, errors}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        // eslint-disable-next-line no-script-url
                        <Form noValidate={true} action={'javascript:void'}>
                            <div className={"t-form-controls-wrapper"}>
                                <div className={'form-sub-title'}>Basic Information</div>
                                <Field name={'name'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Product Name'}
                                                placeholder={'Enter Product Name'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )}
                                </Field>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'panelId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'Panel Name'}
                                                        placeholder={'Select Panel Name'}
                                                        data={testPanelList}
                                                        multiple={false}
                                                        valueKey={"id"}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'productCode'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        fullWidth={true}
                                                        label={'Product Code'}
                                                        required={true}
                                                        placeholder={'Enter Product Code'}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'quantity'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        fullWidth={true}
                                                        label={'Available Quantity'}
                                                        required={true}
                                                        placeholder={'Enter Available Quantity'}
                                                        formikField={field}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'alertLevelStock'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        fullWidth={true}
                                                        label={'Alert Level Quantity'}
                                                        required={true}
                                                        placeholder={'Enter Alert Level Quantity'}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'price'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        fullWidth={true}
                                                        label={'Retail Price'}
                                                        required={true}
                                                        placeholder={'Enter Retail Price'}
                                                        formikField={field}
                                                        prefix={'$'}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                    </Grid>
                                </Grid>
                                <Field name={'description'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                fullWidth={true}
                                                label={'Description'}
                                                multiline={true}
                                                rows={3}
                                                required={true}
                                                placeholder={'Enter Description'}
                                                formikField={field}
                                            />
                                        )}
                                </Field>
                                <FormSubTitleWithTooltipComponent title={"Product Images*"}
                                                                  tooltipData={"Product Images"}/>
                                <input type={"file"}
                                       className={"display-none"}
                                       ref={productImageFileRef}
                                       accept={"image/jpeg,image/gif,image/png"}
                                       onChange={(event) => {
                                           handleTemplateAttachmentUpload(event);
                                       }}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={6}>
                                        <ButtonComponent
                                            variant={"outlined"}
                                            fullWidth={false}
                                            color={'secondary'}
                                            isLoading={productImageAddingInProgress}
                                            handleClick={() => {
                                                productImageFileRef.current?.click()
                                            }}
                                            startIcon={<FileUploadIcon/>}
                                            type={'button'}>
                                            Upload
                                        </ButtonComponent>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={6}>
                                    </Grid>
                                </Grid>
                                <div className={'product-images-wrapper mrg-top-20'}>
                                    {
                                        productImages.map((image: any, index: number) => {
                                            return (
                                                <div key={index} className={'mrg-bottom-20'}>
                                                    <ChipComponent
                                                        label={'product_image'}
                                                        disabled={productImageRemovingInProgress}
                                                        onDelete={() => {
                                                            handleProductImageRemove(image);
                                                        }}
                                                        icon={<AttachFile/>}
                                                    />
                                                </div>
                                            )
                                        })
                                    }
                                </div>

                                <div className={'horizontal-line'}>
                                </div>
                                <div className={'form-sub-title'}>Additional Details</div>
                                <Field name={'productCatalogLink'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Product Catalog Link'}
                                                placeholder={'Enter Product Catalog Link'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )}
                                </Field>
                                <Grid container spacing={2} xs={12}>
                                    <Grid item xs={12} sm={12} md={12}
                                          xl={12}>
                                        <Field name={'tagLine'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Product Tagline'}
                                                        placeholder={'Enter Product Tagline'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2} xs={12}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'methodOfCollection'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Select Collection Process'}
                                                        placeholder={'Select Collection Process'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>

                                        <Field name={'resultingDays'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Resulting Days'}
                                                        placeholder={'Enter Resulting Days'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                        suffix={'days'}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                </Grid>
                                <div className={'horizontal-line'}></div>
                                <div className={'form-sub-title mrg-bottom-25'}>Test Analytes</div>
                                <FieldArray name="analytes"
                                            render={(arrayHelpers: any) => (
                                                <>
                                                    {values?.analytes && values?.analytes.map((analyte: any, index: any) => {
                                                        return (
                                                            <div key={index} className={''}>
                                                                <Grid container spacing={2} xs={12}>
                                                                    <Grid item xs={12} sm={12} md={6}
                                                                          xl={6}>
                                                                        <Field
                                                                            name={`analytes[${index}].analyte`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        label={`Analyte Name`}
                                                                                        placeholder={`Enter Analyte Name`}
                                                                                        type={"text"}
                                                                                        required={true}
                                                                                        formikField={field}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={12} md={6}
                                                                          xl={6}>
                                                                        <ButtonComponent
                                                                            type="button"
                                                                            color={"error"}
                                                                            variant={"outlined"}
                                                                            className={'mrg-top-10'}
                                                                            fullWidth={false}
                                                                            startIcon={<RemoveCircleIcon/>}
                                                                            id="delete_icd_code_btn"
                                                                            handleClick={() => {
                                                                                arrayHelpers.remove(index)
                                                                                if (index === 0 && values?.analytes?.length === 1) {
                                                                                    arrayHelpers.push({analyte: ''})
                                                                                }
                                                                            }}
                                                                        >
                                                                            Remove
                                                                        </ButtonComponent>
                                                                    </Grid>
                                                                </Grid>
                                                                {
                                                                    index === values?.analytes?.length - 1 && (
                                                                        <div className={''}>
                                                                            <ButtonComponent
                                                                                type="button"
                                                                                color={"secondary"}
                                                                                variant={"text"}
                                                                                fullWidth={false}
                                                                                startIcon={<AddIcon/>}
                                                                                id="add_analyte_btn"
                                                                                handleClick={() => {
                                                                                    arrayHelpers.push({analyte: ''})
                                                                                }}
                                                                            >
                                                                                Add More
                                                                            </ButtonComponent>
                                                                        </div>)
                                                                }
                                                            </div>
                                                        )

                                                    })
                                                    }
                                                </>
                                            )}
                                />
                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25 mrg-right-10"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    handleClick={() => {
                                        onEditCancel();
                                    }}
                                    fullWidth={false}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && (dirty || isProductImagesChanged)) || productImages.length === 0 }
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>

                        </Form>
                    );
                }}
            </Formik>
        </div>
    );

};

export default ProductDetailsEditComponent;
